import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Mail, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";

function EmailVerificationPending() {
  const { t, locale } = useTranslation();
  const loginPath = locale === 'tr' ? '/tr/giris' : `/${locale}/login`;
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  
  // Get email from localStorage (set during registration)
  const userEmail = localStorage.getItem('pending_verification_email') || '';

  const handleResend = async () => {
    if (!userEmail) {
      toast.error(t('emailverify.error_no_email'));
      return;
    }

    setResending(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
        email: userEmail
      });

      if (response.data.status === 'success') {
        toast.success(t('emailverify.resend_success'));
      } else if (response.data.status === 'already_verified') {
        toast.info(getReadableErrorMessage({ response: { data: { code: response.data.code } } }, t, 'emailverify.already_verified'));
        navigate(loginPath);
      }
    } catch (error) {
      console.error("Resend error:", error);
      toast.error(getReadableErrorMessage(error, t, 'emailverify.resend_error'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-2 border-[#FFDD59]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="w-16 h-16 text-[#FFDD59]" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#FFDD59]">
            {t('emailverify.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-white text-center">
              <strong className="block mb-2">{userEmail}</strong>
              <span className="block">{t('emailverify.sent_to')}</span>
            </p>
          </div>

          <div className="space-y-3 text-gray-300 text-sm">
            <p className="font-semibold text-white">📧 {t('emailverify.steps_title')}</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>{t('emailverify.step1')}</li>
              <li>{t('emailverify.step2')}</li>
              <li>{t('emailverify.step3')}</li>
              <li>{t('emailverify.step4')}</li>
            </ol>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-xs text-yellow-300">
              <strong>⚠️ {t('emailverify.note_label')}</strong> {t('emailverify.note')}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              onClick={handleResend}
              disabled={resending}
              variant="outline"
              className="w-full border-[#FFDD59] text-[#FFDD59] hover:bg-[#FFDD59] hover:text-black"
            >
              {resending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('emailverify.sending')}
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('emailverify.btn_resend')}
                </>
              )}
            </Button>

            <Button
              onClick={() => navigate(loginPath)}
              variant="ghost"
              className="w-full text-gray-400 hover:bg-white hover:text-black"
            >
              {t('emailverify.btn_back_login')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailVerificationPending;
